import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Card from "../components/ui/Card";
import Button from "../components/ui/Button";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import { useToast } from "../hooks/useToast";
import { getVehicleById, createInvoice, getInvoicesByVehicle } from "../services/api";

interface Invoice {
  id: number;
  vehicleId: number;
  description: string;
  amount: number;
  createdAt: string;
}

const CreateInvoiceImproved: React.FC = () => {
  const { vehicleId } = useParams<{ vehicleId: string }>();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState<any>(null);
  const [amount, setAmount] = useState<number | "">("");
  const [description, setDescription] = useState<string>("");
  const [lastInvoices, setLastInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { showToast, ToastContainer } = useToast();

  const fetchVehicle = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getVehicleById(vehicleId!);
      setVehicle(data);
    } catch (err: any) {
      setError(err?.message || "Failed to fetch vehicle");
      showToast(err?.message || "Failed to fetch vehicle", "error");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, showToast]);

  const fetchInvoices = useCallback(async () => {
    if (!vehicleId) return;
    setLoading(true);
    setError("");
    try {
      const vehicleIdNum = Number(vehicleId);
      let data = await getInvoicesByVehicle(vehicleIdNum);
      let invoices = data.sort((a: Invoice, b: Invoice) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
      setLastInvoices(invoices.slice(0, 5));
    } catch (err: any) {
      setError(err?.message || "Failed to fetch invoices");
      showToast(err?.message || "Failed to fetch invoices", "error");
    } finally {
      setLoading(false);
    }
  }, [vehicleId, showToast]);

  useEffect(() => {
    fetchVehicle();
    fetchInvoices();
    // eslint-disable-next-line
  }, [fetchVehicle, fetchInvoices]);

  const handleSubmit = async () => {
    if (!amount || amount <= 0) {
      showToast("Enter a valid invoice amount", "warning");
      return;
    }
    if (!description) {
      showToast("Enter invoice description", "warning");
      return;
    }
    if (!vehicle) return;
    if (!window.confirm(`Confirm creating invoice of ₹${amount} for ${vehicle.vehicleNo}?`)) return;
    setLoading(true);
    setError("");
    try {
      await createInvoice({
        vehicleId: vehicle.id,
        amount,
        description,
      });
      showToast("Invoice created successfully!", "success");
      setAmount("");
      setDescription("");
      fetchInvoices();
    } catch (err: any) {
      setError(err?.message || "Error creating invoice");
      showToast(err?.message || "Error creating invoice", "error");
    } finally {
      setLoading(false);
    }
  };

  if (!vehicle) {
    return (
      <div className="min-h-screen w-full bg-gray-50 flex flex-col">
        <ToastContainer />
        <Card className="w-full max-w-5xl mx-auto p-4 md:p-6">
          <div className="flex items-center mb-4">
            <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
            <h1 className="text-2xl font-bold text-blue-700">Create Invoice</h1>
          </div>
          {loading ? <LoadingSpinner /> : <div className="text-gray-500">Loading vehicle details...</div>}
          {error && <div className="text-red-600 mb-2">{error}</div>}
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-gray-50 flex flex-col">
      <ToastContainer />
      <Card className="w-full max-w-5xl mx-auto p-4 md:p-6">
        <div className="flex items-center mb-4">
          <Button color="secondary" onClick={() => navigate(-1)} className="mr-4">Back</Button>
          <h1 className="text-2xl font-bold text-blue-700">Create Invoice</h1>
        </div>
        {loading && <LoadingSpinner />}
        {error && <div className="text-red-600 mb-2">{error}</div>}
        <div className="mb-6">
          <h2 className="text-lg font-semibold mb-2">Vehicle Info</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <p><span className="font-semibold text-gray-600">Vehicle No:</span> {vehicle.vehicleNo}</p>
            <p><span className="font-semibold text-gray-600">Owner:</span> {vehicle.ownerName}</p>
            <p><span className="font-semibold text-gray-600">Type:</span> {vehicle.vehicleType}</p>
            <p><span className="font-semibold text-gray-600">Balance:</span> ₹{vehicle.balance}</p>
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6 w-full flex flex-col gap-4 mb-6">
          <input
            type="number"
            placeholder="Invoice Amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value === "" ? "" : Number(e.target.value))}
            className="border p-3 rounded w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <input
            type="text"
            placeholder="Invoice Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border p-3 rounded w-full focus:outline-none focus:ring-2 focus:ring-blue-400"
          />
          <Button
            style={{ background: "#22c55e", color: "#fff" }}
            className="w-full py-3 rounded-lg text-base font-semibold shadow hover:bg-green-700 transition"
            onClick={handleSubmit}
            loading={loading}
          >
            Confirm Invoice
          </Button>
        </div>
        <div>
          <h2 className="text-xl font-semibold mb-3">Last 5 Invoices</h2>
          {lastInvoices.length === 0 ? (
            <p className="text-gray-500">No previous invoices found.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white shadow rounded text-sm md:text-base">
                <thead className="hidden md:table-header-group">
                  <tr className="bg-gray-100 text-left">
                    <th className="py-2 px-4">ID</th>
                    <th className="py-2 px-4">Description</th>
                    <th className="py-2 px-4">Amount (₹)</th>
                    <th className="py-2 px-4">Date & Time</th>
                  </tr>
                </thead>
                <tbody>
                  {lastInvoices.map((inv) => (
                    <tr
                      key={inv.id}
                      className="border-b md:table-row flex flex-col md:flex-row mb-4 md:mb-0 p-4 md:p-0 rounded md:rounded-none hover:bg-blue-50 transition"
                    >
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">ID: </span>
                        {inv.id}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Description: </span>
                        {inv.description}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Amount: </span>
                        ₹{inv.amount}
                      </td>
                      <td className="py-2 px-0 md:px-4">
                        <span className="md:hidden text-gray-500">Date: </span>
                        {new Date(inv.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default CreateInvoiceImproved;
